import {
  constructList,
  getCategories,
  getText,
  sortAndSliceList,
  appendGOATandCountryCodeToTitle,
  stripSpecialCharacterFromString,
} from 'shared/utils/pageMetadataUtils';
import { startCase } from 'shared/utils/startCase';
import { t } from 'localization/utils/t';

const TP = 'shared.utils.getBrandCategoryMetadata';

export const getBrandCategoryMetadata = ({
  pageTitle,
  pageDescription,
  constructorResponse,
  query,
  locale,
}) => {
  const { groups = [], results = [] } = constructorResponse?.response || {};
  const { subCategories } = getCategories(groups);
  const brandName: string = startCase(query?.brandName);
  const topLevelCategory: string =
    groups?.[0]?.group_id === 'sneakers'
      ? t(`${TP}.shoes`, 'shoes')
      : stripSpecialCharacterFromString(groups?.[0]?.group_id);
  const topLevelCategoryWithStartCase: string = startCase(topLevelCategory);
  const topThreePTs = constructList({ list: sortAndSliceList(results), key: 'value' });

  // Pulling out static strings for translation purposes
  const including = t(`${TP}.including`, 'including');
  const more = t(`${TP}.more`, 'More');

  const theTopThreePTs = getText('', topThreePTs?.constructedList);
  const discoverNewReleasesLikeTopThreePTs =
    theTopThreePTs?.length > 0
      ? t(
          `${TP}.discoverNewReleasesLikeTopThreePTs`,
          'Discover new releases like {theTopThreePTs}.',
          { theTopThreePTs },
        )
      : '';
  const featuringTheTopThreePTs =
    theTopThreePTs?.length > 0
      ? t(`${TP}.featuringTheTopThreePTs`, 'Featuring the {theTopThreePTs}.', { theTopThreePTs })
      : '';

  if (query?.subCategory) {
    const title = t(
      `${TP}.title`,
      'Buy {brandName} {topLevelCategoryWithStartCase}: New Releases & Iconic Styles',
      {
        brandName,
        topLevelCategoryWithStartCase,
      },
    );
    const description = t(
      `${TP}.description`,
      'Shop {brandName} {topLevelCategory} on GOAT. {featuringTheTopThreePTs} Authenticity assured.',
      {
        brandName,
        topLevelCategory,
        featuringTheTopThreePTs,
      },
    );

    return {
      pageTitle: pageTitle || appendGOATandCountryCodeToTitle(title, locale),
      pageDescription: pageDescription || description,
    };
  }

  if (query?.pageSlug === 'sneakers' && !query?.subCategory) {
    const includingTopThreeSubCategories = getText(including, subCategories?.constructedList);
    const title = t(
      `${TP}.title2`,
      'Buy {brandName} {topLevelCategoryWithStartCase}: New & Pre-Owned',
      {
        brandName,
        topLevelCategoryWithStartCase,
      },
    );
    const description = t(
      `${TP}.description2`,
      'Shop {brandName} {topLevelCategory} on GOAT {includingTopThreeSubCategories}. {discoverNewReleasesLikeTopThreePTs} Authenticity assured.',
      {
        brandName,
        topLevelCategory,
        includingTopThreeSubCategories,
        discoverNewReleasesLikeTopThreePTs,
      },
    );

    return {
      pageTitle: pageTitle || appendGOATandCountryCodeToTitle(title, locale),
      pageDescription: pageDescription || description,
    };
  }

  if (query?.pageSlug !== 'sneakers' && !query?.subCategory) {
    const _subCategories = subCategories?.constructedListWithStartCase;
    if (_subCategories?.length > 1) {
      _subCategories[_subCategories?.length - 1] = `${more}`;
    }

    const colonWithTopThreeSubCategories = getText(':', _subCategories);
    const includingTopThreeSubCategories = getText(including, subCategories?.constructedList);

    const title = t(
      `${TP}.title3`,
      'Buy {brandName} {topLevelCategoryWithStartCase}{colonWithTopThreeSubCategories}',
      {
        brandName,
        topLevelCategoryWithStartCase,
        colonWithTopThreeSubCategories,
      },
    );
    const description = t(
      `${TP}.description3`,
      'Shop {brandName} {topLevelCategory} on GOAT {includingTopThreeSubCategories}. Buyer protection guaranteed on all purchases.',
      {
        brandName,
        topLevelCategory,
        includingTopThreeSubCategories,
      },
    );

    return {
      pageTitle: pageTitle || appendGOATandCountryCodeToTitle(title, locale),
      pageDescription: pageDescription || description,
    };
  }

  return { pageTitle, pageDescription };
};
